import { cn } from '@/lib/cn';
import { Loader } from '@heidi/design-system/loader';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import { Hand, Pencil } from 'lucide-react';

import type { SidebarNavRowStatus } from './sidebar-nav-row-status.types';

export type { SidebarNavRowStatus } from './sidebar-nav-row-status.types';

const STATUS_LABEL: Record<Exclude<SidebarNavRowStatus, 'rest'>, string> = {
  'needs-attention': 'Needs your attention',
  draft: 'Draft',
  streaming: 'Chat is thinking',
};

export function SidebarNavRowStatusIcon({
  status,
  showTooltip = false,
  className,
}: {
  status: SidebarNavRowStatus;
  showTooltip?: boolean;
  className?: string;
}): JSX.Element {
  const slotClass = cn('flex size-4 shrink-0 items-center justify-center', className);

  if (status === 'rest') {
    return (
      <span className={slotClass} aria-hidden>
        <span className="block size-2 rounded-full border border-text-tertiary" />
      </span>
    );
  }

  const label = STATUS_LABEL[status];

  let icon: JSX.Element;
  switch (status) {
    case 'needs-attention':
      icon = <Hand className="size-3 text-text-primary" aria-hidden />;
      break;
    case 'draft':
      icon = <Pencil className="size-3 text-text-tertiary" aria-hidden />;
      break;
    case 'streaming':
      icon = <Loader size={14} strokeWidth={2} />;
      break;
  }

  const indicator = (
    <span
      className={slotClass}
      aria-label={label}
      title={showTooltip ? undefined : label}
    >
      {icon}
    </span>
  );

  if (!showTooltip) return indicator;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{indicator}</TooltipTrigger>
      <TooltipContent side="right" sideOffset={8}>
        {label}
        <TooltipArrow />
      </TooltipContent>
    </Tooltip>
  );
}
